import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface PpkStatusResponse {
  ok: boolean;
  status?: string;
  message?: string;
  files?: {
    roverObsUrl?: string;
    baseObsUrl?: string;
    roverNavUrl?: string;
    resultPosUrl?: string;
  };
}

export interface PpkUploadResponse {
  ok: boolean;
  message?: string;
  files?: {
    roverObsUrl?: string;
    baseObsUrl?: string;
    roverNavUrl?: string;
  };
  uploadUrl?: string;
  targetKey?: string;
}

export interface PpkStartResponse {
  ok: boolean;
  status?: string;
  statusCode?: number;
  message?: string;
}

export interface PpkConvertFile {
  name: string;
  url: string;
}

export interface PpkConvertFilesResponse {
  ok: boolean;
  status?: string; // IDLE | RUNNING | SUCCESS | FAILED
  message?: string;
  files?: PpkConvertFile[];
}

export interface PpkConvertStartResponse {
  ok: boolean;
  status?: string; // STARTED
  statusCode?: number;
  message?: string;
}

export interface DownloaderFile {
  mountpoint: string;
  filename: string;
  size: number;
  lastModified: string;
}

export interface DownloaderFilesResponse {
  ok: boolean;
  message?: string;
  files: DownloaderFile[];
}

@Injectable({ providedIn: 'root' })
export class PpkService {

  private readonly baseUrl = `${environment.apiBaseUrl}/ppk`;

  constructor(private readonly http: HttpClient) {}

  // -------- status / process --------
  getStatus(): Observable<PpkStatusResponse> {
    return this.http.get<PpkStatusResponse>(`${this.baseUrl}/status`);
  }

  startRnx2rtkp(): Observable<PpkStartResponse> {
    return this.http.post<PpkStartResponse>(`${this.baseUrl}/rnx2rtkp/start`, {});
  }

  // -------- export / convert --------
  startConvertFiles(): Observable<PpkConvertStartResponse> {
    return this.http.post<PpkConvertStartResponse>(`${this.baseUrl}/convert/files`, {});
  }

  getConvertFiles(): Observable<PpkConvertFilesResponse> {
    return this.http.get<PpkConvertFilesResponse>(`${this.baseUrl}/convert/files`);
  }

  deleteConvertFile(name: string): Observable<{ ok: boolean; message?: string }> {
    const url = `${this.baseUrl}/convert/file?name=${encodeURIComponent(name)}`;
    return this.http.delete<{ ok: boolean; message?: string }>(url);
  }

  deleteAllConvertFiles(): Observable<{ ok: boolean; message?: string }> {
    return this.http.delete<{ ok: boolean; message?: string }>(`${this.baseUrl}/convert/files`);
  }

  // -------- upload URLs (pre-signed PUT) --------
  getRoverUploadUrl(kind: 'ubx' | 'obs'): Observable<PpkUploadResponse> {
    return this.http.post<PpkUploadResponse>(`${this.baseUrl}/rover/upload-url`, { kind });
  }

  getBaseUploadUrl(kind: 'rtcm3' | 'obs', baseStartDate?: string | null): Observable<PpkUploadResponse> {
    const body: any = { kind };
    if (baseStartDate) {
      body.baseStartDate = baseStartDate;
    }
    return this.http.post<PpkUploadResponse>(`${this.baseUrl}/base/upload-url`, body);
  }

  getNavUploadUrl(): Observable<PpkUploadResponse> {
    return this.http.post<PpkUploadResponse>(`${this.baseUrl}/nav/upload-url`, {});
  }

  // -------- completion / convbin triggers --------
  notifyRover(kind: 'ubx' | 'obs'): Observable<PpkUploadResponse> {
    return this.http.post<PpkUploadResponse>(`${this.baseUrl}/convbin/ubx`, { kind });
  }

  notifyBase(kind: 'rtcm3' | 'obs', baseStartDate?: string | null): Observable<PpkUploadResponse> {
    const body: any = { kind };
    if (baseStartDate) {
      body.baseStartDate = baseStartDate;
    }
    return this.http.post<PpkUploadResponse>(`${this.baseUrl}/convbin/rtcm3`, body);
  }

  notifyNav(): Observable<PpkUploadResponse> {
    return this.http.post<PpkUploadResponse>(`${this.baseUrl}/nav/complete`, {});
  }

  // Deletion
  deleteRover(): Observable<PpkStatusResponse> {
    return this.http.delete<PpkStatusResponse>(`${this.baseUrl}/rover`);
  }

  deleteBase(): Observable<PpkStatusResponse> {
    return this.http.delete<PpkStatusResponse>(`${this.baseUrl}/base`);
  }

  deleteNav(): Observable<PpkStatusResponse> {
    return this.http.delete<PpkStatusResponse>(`${this.baseUrl}/nav`);
  }

  deleteResult(): Observable<PpkStatusResponse> {
    return this.http.delete<PpkStatusResponse>(`${this.baseUrl}/result`);
  }

  // -------- Downloader Integration --------
  listDownloaderFiles(): Observable<DownloaderFilesResponse> {
    return this.http.get<DownloaderFilesResponse>(`${this.baseUrl}/downloader/files`);
  }

  copyDownloaderFile(mountpoint: string): Observable<PpkStatusResponse> {
    return this.http.post<PpkStatusResponse>(`${this.baseUrl}/downloader/copy`, { mountpoint });
  }
}


